import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { HAL, prng, useIndeling } from './indeling'
import { useAnimatie, useMaterialen, useObjecten } from '../scene/store'
import { useMerkTokens } from '../kiosk/useMerkTokens'
import { maakPalletGeometrie } from './Lading'
import { pendelX } from './Palletpendel'
import { gaas } from './texturen'
import { veiligheidsbord, type Bordsoort } from './etiketten'
import { dockOpen } from './daglicht'
import { halklok, rondHz, rondOm } from './omloop'

/**
 * Wat hier draait en knippert op de klok van de hal, met frequenties die een
 * geheel aantal keer rondgaan per omloop (`omloop.ts`): de film van de
 * zweefronde loopt dan naadloos, en het realtime-3D past erop.
 */
const ROL = rondOm(3.2)
const LED = rondOm(2.2)
const BAND = rondHz(0.28)

/**
 * De honderd kleine dingen. Bollards, vangrails, dockdeuren, een transportband,
 * de laadstraat, palletstapels, rolcontainers, afvalbakken en bebording.
 *
 * Twee groepen zijn geen decoratie maar inhoud: zonder bollards en zebrapaden
 * kan Veiligheid zijn verhaal niet vertellen, en zonder laadstraat heeft Energie
 * geen plek om op te lichten.
 */
export function Props() {
  const ind = useIndeling()
  const obj = useObjecten()
  const mat = useMaterialen()
  const anim = useAnimatie()
  const tokens = useMerkTokens()

  const rollen = useRef<THREE.Group>(null)
  const deuren = useRef<THREE.Group>(null)
  const laadLeds = useRef<THREE.Group>(null)

  // Hoogte en scheefstand van elke stapel, en de draaiing van elke
  // rolcontainer, vooraf. Deze getallen werden tijdens het renderen getrokken,
  // en dan krijgt elke hertekening — dus elke tik op een tegel — nieuwe: de
  // stapels groeiden en krompen bij iedere navigatie, en React bouwde de
  // pallets telkens opnieuw op.
  const varianten = useMemo(() => {
    const r = prng(ind.cfg.seed + 991)
    return {
      stapels: Array.from({ length: 7 }, () =>
        Array.from({ length: 5 + Math.floor(r() * 6) }, () => [(r() - 0.5) * 0.04, (r() - 0.5) * 0.04, (r() - 0.5) * 0.05] as const),
      ),
      draaiing: Array.from({ length: 9 }, () => r() * 3),
    }
  }, [ind.cfg.seed])

  // Echte pallets in de stapels, gaas in de rolcontainers en echte borden aan
  // de wand. De stapels waren massieve blokken, de rolcontainers een
  // wireframe — een debugweergave die in beeld was blijven staan.
  const middelen = useMemo(() => {
    const g = gaas()
    g.repeat.set(5, 11)
    const soorten: Bordsoort[] = ['heftruck', 'hesje', 'gehoor', 'rookverbod']
    return {
      pallet: maakPalletGeometrie(),
      gaas: g,
      borden: soorten.map((s) => veiligheidsbord(s)),
    }
  }, [])
  useEffect(() => () => {
    middelen.pallet.dispose(); middelen.gaas.dispose(); middelen.borden.forEach((b) => b.dispose())
  }, [middelen])

  // Posities één keer berekenen
  const plaatsing = useMemo(() => {
    const r = prng(ind.cfg.seed + 4242)
    const rand = ind.blokDiepte + 3.2
    // Niet op de route van de C-MATIC, die voorbij zijn stations keert.
    const pendel = pendelX(ind)
    const vrij = (x: number) => (Math.abs(x - pendel) < 2 ? x + (x < pendel ? -2 : 2) : x)
    return {
      palletstapels: Array.from({ length: 7 }, () => [
        vrij(-24 + r() * 48), 0, (r() > 0.5 ? 1 : -1) * (rand + r() * 4),
      ] as [number, number, number]),
      rolcontainers: Array.from({ length: 9 }, () => [
        vrij(-22 + r() * 44), 0, (r() > 0.5 ? 1 : -1) * (rand + r() * 5),
      ] as [number, number, number]),
      afvalbakken: Array.from({ length: 4 }, () => [
        vrij(-20 + r() * 40), 0, (r() > 0.5 ? 1 : -1) * (rand + 1 + r() * 3),
      ] as [number, number, number]),
    }
  }, [ind])

  useFrame(() => {
    const t = halklok.t

    // Transportbandrollen draaien
    if (rollen.current && anim.transportband) {
      rollen.current.children.forEach((r) => { r.rotation.z = -t * ROL })
    }

    // Dockdeuren gaan traag open en dicht, elk met een eigen fase; het
    // daglicht erdoor (`Effecten.tsx`) rekent met dezelfde `dockOpen`.
    if (deuren.current && anim.dockdeuren) {
      deuren.current.children.forEach((d, i) => {
        const open = dockOpen(t, i)
        d.position.y = 2.1 + open * 2.6
        d.scale.y = Math.max(0.08, 1 - open * 0.85)
      })
    }

    // Laadindicatie pulseert
    if (laadLeds.current && anim.laadindicatie) {
      laadLeds.current.children.forEach((l, i) => {
        const m = (l as THREE.Mesh).material as THREE.MeshBasicMaterial
        m.opacity = 0.35 + Math.sin(t * LED + i * 1.3) * 0.35 + 0.3
      })
    }
  })

  const rand = ind.blokDiepte + 2.2

  return (
    <group>
      {/* ---- Bollards langs de gangpadkoppen ---- */}
      {obj.bollards && (
        <group>
          {Array.from({ length: ind.cfg.gangpaden }).flatMap((_, i) =>
            [-1, 1].map((zijde) => (
              <mesh
                key={`${i}-${zijde}`}
                position={[zijde * (HAL.breedte * 0.44), 0.45, ind.gangpadZ(i)]}
                castShadow
              >
                <cylinderGeometry args={[0.11, 0.13, 0.9, 12]} />
                <meshStandardMaterial color="#F5B301" roughness={0.6} metalness={0.15} />
              </mesh>
            )),
          )}
        </group>
      )}

      {/* ---- Vangrail langs de voetgangersroute ---- */}
      {obj.vangrails && (
        <group position={[0, 0, rand + 6]}>
          {Array.from({ length: 14 }).map((_, i) => (
            <group key={i} position={[-26 + i * 4, 0, 0]}>
              <mesh position={[0, 0.55, 0]} castShadow>
                <boxGeometry args={[0.1, 1.1, 0.1]} />
                <meshStandardMaterial color="#F5B301" roughness={0.6} />
              </mesh>
              <mesh position={[2, 0.85, 0]}>
                <boxGeometry args={[4, 0.1, 0.06]} />
                <meshStandardMaterial color="#F5B301" roughness={0.6} />
              </mesh>
              <mesh position={[2, 0.45, 0]}>
                <boxGeometry args={[4, 0.1, 0.06]} />
                <meshStandardMaterial color="#F5B301" roughness={0.6} />
              </mesh>
            </group>
          ))}
        </group>
      )}

      {/* ---- Dockdeuren in de achterwand ---- */}
      {obj.dockdeuren && (
        <group>
          {[-18, -6, 6, 18].map((x) => (
            <group key={x} position={[x, 0, -HAL.diepte * 0.88]}>
              {/* Kozijn */}
              <mesh position={[0, 2.2, 0.1]}>
                <boxGeometry args={[3.4, 4.4, 0.16]} />
                <meshStandardMaterial color="#2a2f36" roughness={0.7} />
              </mesh>
              {/* Doorkijk naar buiten: koel licht */}
              <mesh position={[0, 2.1, 0.02]}>
                <planeGeometry args={[3.0, 4.0]} />
                <meshBasicMaterial color="#8fa8c8" toneMapped={false} />
              </mesh>
              {/* Levellers */}
              <mesh position={[0, 0.18, 0.9]} receiveShadow>
                <boxGeometry args={[3.0, 0.14, 1.8]} />
                <meshStandardMaterial color="#3d434b" roughness={0.65} metalness={0.4} />
              </mesh>
            </group>
          ))}
          {/* De beweegbare deurbladen, apart zodat ze animeren */}
          <group ref={deuren}>
            {[-18, -6, 6, 18].map((x) => (
              <mesh key={x} position={[x, 2.1, -HAL.diepte * 0.88 + 0.06]} castShadow>
                <boxGeometry args={[3.0, 4.0, 0.08]} />
                <meshStandardMaterial color="#5b626b" roughness={0.55} metalness={0.45} />
              </mesh>
            ))}
          </group>
        </group>
      )}

      {/* ---- Laadstraat: de plek waar de Energie-module zich afspeelt ---- */}
      {obj.laadstraat && (
        <group position={[-HAL.breedte * 0.42, 0, rand + 2]}>
          {Array.from({ length: 6 }).map((_, i) => (
            <group key={i} position={[0, 0, i * 2.6]}>
              <mesh position={[0, 0.85, 0]} castShadow>
                <boxGeometry args={[0.55, 1.7, 0.42]} />
                <meshStandardMaterial color="#343942" roughness={0.45} metalness={0.5} />
              </mesh>
              <mesh position={[0, 1.42, 0.23]}>
                <planeGeometry args={[0.34, 0.24]} />
                <meshBasicMaterial color="#10161d" />
              </mesh>
              {/* Kabel naar de vloer */}
              <mesh position={[0.34, 0.5, 0.1]} rotation={[0, 0, -0.5]}>
                <cylinderGeometry args={[0.035, 0.035, 1.1, 6]} />
                <meshStandardMaterial color="#1a1d22" roughness={0.9} />
              </mesh>
            </group>
          ))}
          <group ref={laadLeds}>
            {Array.from({ length: 6 }).map((_, i) => (
              <mesh key={i} position={[0, 1.55, i * 2.6 + 0.23]}>
                <circleGeometry args={[0.055, 10]} />
                <meshBasicMaterial color={tokens.ok} transparent opacity={0.6} toneMapped={false} />
              </mesh>
            ))}
          </group>
        </group>
      )}

      {/* ---- Transportband met draaiende rollen ---- */}
      {obj.transportband && (
        <group position={[HAL.breedte * 0.36, 0, -rand - 3]}>
          <mesh position={[0, 0.42, 0]} castShadow>
            <boxGeometry args={[0.9, 0.12, 11]} />
            <meshStandardMaterial color="#454b54" roughness={0.5} metalness={0.55} />
          </mesh>
          {[-0.42, 0.42].map((x) => (
            <mesh key={x} position={[x, 0.24, 0]}>
              <boxGeometry args={[0.08, 0.48, 11]} />
              <meshStandardMaterial color="#3a4048" roughness={0.6} metalness={0.5} />
            </mesh>
          ))}
          <group ref={rollen}>
            {Array.from({ length: 34 }).map((_, i) => (
              <mesh key={i} position={[0, 0.5, -5.3 + i * 0.32]} rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[0.055, 0.055, 0.82, 8]} />
                <meshStandardMaterial color="#9aa2ad" roughness={0.35} metalness={0.75} />
              </mesh>
            ))}
          </group>
          {/* Een doos die meerijdt */}
          <BandDoos anim={anim} />
        </group>
      )}

      {/* ---- Stapels lege pallets ---- */}
      {obj.palletstapels && plaatsing.palletstapels.map((p, i) => (
        <group key={i} position={p}>
          {varianten.stapels[i % varianten.stapels.length].map(([dx, dz, ry], l) => (
            <mesh
              key={l}
              geometry={middelen.pallet}
              position={[dx, l * 0.144, dz]}
              rotation={[0, ry, 0]}
              castShadow
              receiveShadow
            >
              <meshStandardMaterial color={mat.pallet} roughness={0.92} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ---- Rolcontainers ---- */}
      {obj.rolcontainers && plaatsing.rolcontainers.map((p, i) => (
        <group key={i} position={p} rotation={[0, varianten.draaiing[i % varianten.draaiing.length], 0]}>
          <mesh position={[0, 0.9, 0]} castShadow>
            <boxGeometry args={[0.72, 1.7, 0.82]} />
            <meshStandardMaterial
              color="#9aa2ab" roughness={0.4} metalness={0.8}
              alphaMap={middelen.gaas} alphaTest={0.5} side={THREE.DoubleSide}
            />
          </mesh>
          {/* Hoekstijlen en wielen: het gaas hangt in een frame. */}
          {([[-0.35, -0.4], [0.35, -0.4], [-0.35, 0.4], [0.35, 0.4]] as const).map(([x, z], k) => (
            <group key={k}>
              <mesh position={[x, 0.95, z]}>
                <boxGeometry args={[0.025, 1.7, 0.025]} />
                <meshStandardMaterial color="#8a929b" roughness={0.45} metalness={0.8} />
              </mesh>
              <mesh position={[x * 0.9, 0.05, z * 0.9]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.05, 0.05, 0.035, 12]} />
                <meshStandardMaterial color="#1c1e21" roughness={0.8} />
              </mesh>
            </group>
          ))}
          <mesh position={[0, 0.12, 0]}>
            <boxGeometry args={[0.74, 0.08, 0.84]} />
            <meshStandardMaterial color="#4c525a" roughness={0.6} metalness={0.5} />
          </mesh>
        </group>
      ))}

      {/* ---- Afvalbakken ---- */}
      {obj.afvalbakken && plaatsing.afvalbakken.map((p, i) => (
        <mesh key={i} position={[p[0], 0.55, p[2]]} castShadow>
          <cylinderGeometry args={[0.36, 0.3, 1.1, 10]} />
          <meshStandardMaterial color={i % 2 ? '#3f6b46' : '#5a4a3a'} roughness={0.8} />
        </mesh>
      ))}

      {/* ---- Bebording aan de wanden ---- */}
      {obj.bebording && [-24, -8, 8, 24].map((x, i) => (
        <group key={x} position={[x, 3.4, -HAL.diepte * 0.895]}>
          {/* Twee borden naast elkaar, zoals ze bij een dockzone hangen. */}
          {[0, 1].map((k) => (
            <mesh key={k} position={[(k - 0.5) * 0.72, 0, 0]}>
              <planeGeometry args={[0.6, 0.75]} />
              <meshStandardMaterial map={middelen.borden[(i * 2 + k) % middelen.borden.length]} roughness={0.55} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  )
}

/** Eén doos die over de band schuift en vooraan opnieuw begint. */
function BandDoos({ anim }: { anim: ReturnType<typeof useAnimatie> }) {
  const doos = useRef<THREE.Mesh>(null)
  useFrame(() => {
    if (!doos.current) return
    const s = anim.aan && anim.transportband ? anim.snelheid : 0
    const t = (halklok.t * BAND) % 1
    doos.current.position.z = -5.3 + t * 10.6
    doos.current.visible = s > 0
  })
  return (
    <mesh ref={doos} position={[0, 0.72, 0]} castShadow>
      <boxGeometry args={[0.6, 0.42, 0.5]} />
      <meshStandardMaterial color="#a89070" roughness={0.88} />
    </mesh>
  )
}
